import { aggregateMetrics, normalizeDailyMetric } from "./metrics";
import { demoPlayers, demoPools, makeDemoRows } from "./demoData";
import { isSupabaseConfigured, supabase } from "./supabase";
import type { DashboardSummary, DateRange, Player, SavedPool } from "../types";

type DbRow = Record<string, unknown>;

const textFrom = (row: DbRow, names: string[], fallback: string): string => {
  for (const name of names) {
    if (typeof row[name] === "string" && row[name]) return row[name];
  }
  return fallback;
};

export async function loadDirectory(): Promise<{
  players: Player[];
  pools: SavedPool[];
}> {
  if (!isSupabaseConfigured || !supabase) {
    return { players: demoPlayers, pools: demoPools };
  }

  const [playersResult, poolsResult, membersResult] = await Promise.all([
    supabase.from("players").select("*"),
    supabase.from("pools").select("*"),
    supabase.from("pool_members").select("pool_id, player_id"),
  ]);

  if (playersResult.error) throw playersResult.error;
  if (poolsResult.error) throw poolsResult.error;
  if (membersResult.error) throw membersResult.error;

  const players: Player[] = (playersResult.data as DbRow[]).map((row, index) => ({
    id: String(row.id),
    name: textFrom(row, ["display_name", "name", "nickname"], `Jogador ${index + 1}`),
    teamId: typeof row.team_id === "string" ? row.team_id : undefined,
  }));

  const membership = new Map<string, string[]>();
  for (const row of membersResult.data as DbRow[]) {
    const poolId = String(row.pool_id);
    const current = membership.get(poolId) ?? [];
    current.push(String(row.player_id));
    membership.set(poolId, current);
  }

  const pools: SavedPool[] = (poolsResult.data as DbRow[]).map((row, index) => ({
    id: String(row.id),
    name: textFrom(row, ["name", "pool_name"], `Pool ${index + 1}`),
    teamId: typeof row.team_id === "string" ? row.team_id : undefined,
    playerIds: membership.get(String(row.id)) ?? [],
  }));

  return { players, pools };
}

export async function loadSummary(
  range: DateRange,
  playerIds: string[],
): Promise<DashboardSummary> {
  if (!isSupabaseConfigured || !supabase) {
    return aggregateMetrics(makeDemoRows(range));
  }

  let query = supabase
    .from("daily_player_metrics")
    .select("*")
    .gte("metric_date", range.from)
    .lte("metric_date", range.to)
    .order("metric_date", { ascending: true });

  if (playerIds.length > 0) query = query.in("player_id", playerIds);

  const { data, error } = await query;
  if (error) throw error;
  return aggregateMetrics((data as DbRow[]).map(normalizeDailyMetric));
}

export async function createPool(
  name: string,
  players: Player[],
  playerIds: string[],
): Promise<SavedPool> {
  if (!isSupabaseConfigured || !supabase) {
    return { id: crypto.randomUUID(), name, playerIds };
  }

  const teamId = players.find((player) => playerIds.includes(player.id))?.teamId;
  if (!teamId) throw new Error("Não foi possível identificar o time da pool.");

  const user = (await supabase.auth.getUser()).data.user;
  const payload: Record<string, string> = { name, team_id: teamId };
  if (user) payload.created_by = user.id;

  let result = await supabase.from("pools").insert(payload).select("id").single();
  if (result.error && payload.created_by) {
    delete payload.created_by;
    result = await supabase.from("pools").insert(payload).select("id").single();
  }
  if (result.error) throw result.error;

  const poolId = String((result.data as { id: string }).id);
  const members = playerIds.map((playerId) => ({ pool_id: poolId, player_id: playerId }));
  const memberResult = await supabase.from("pool_members").insert(members);
  if (memberResult.error) throw memberResult.error;

  return { id: poolId, name, playerIds, teamId };
}
